
import {Component, Vue} from 'vue-property-decorator';
import {
  readHasModuleAccess,
  readUserProfile,
  readUserProfileStats,
  readVisitedPages,
} from '@/store/main/getters';

import {dispatchGetUserProfile, dispatchGetUserProfileStats} from '@/store/main/actions';
import DashboardChart from '@/components/stats-dashboard/DashboardChart.vue';
import {IUserStatsItem} from '@/interfaces/user';

@Component({
  components: {DashboardChart},
})
export default class Dashboard extends Vue {
  private barColors = ['#1976d2', '#00a896', '#f9a825', '#ef5350', '#7e57c2', '#26a69a', '#5c6bc0', '#ff7043'];

  private get greetedUser() {
    const userProfile = readUserProfile(this.$store);
    if (userProfile) {
      if (userProfile.fullname) {
        return userProfile.fullname;
      } else {
        return userProfile.email;
      }
    }
    return 'usuário';
  }

  private get stats() {
    return readUserProfileStats(this.$store);
  }

  private get summarized() {
    return this.stats?.summarized;
  }

  private get detailed() {
    return this.stats?.detailed;
  }

  private get isAdminDashboard() {
    return this.hasModuleAccess({module: 'dashboard_admin', method: 'show'});
  }

  private get kpiCards() {
    const summarized = this.summarized;
    if (!summarized) {
      return [];
    }
    return [
      {
        key: 'active',
        label: 'Usuários ativos',
        value: summarized.total_active_users || 0,
        icon: 'mdi-account-check',
        className: 'is-success',
      },
      {
        key: 'inactive',
        label: 'Usuários inativos',
        value: summarized.total_inactive_users || 0,
        icon: 'mdi-account-off',
        className: 'is-muted',
      },
      {
        key: 'pending-users',
        label: 'Usuários pendentes',
        value: summarized.total_external_users_unregistered || 0,
        icon: 'mdi-account-clock',
        className: 'is-danger',
      },
      {
        key: 'pending-batches',
        label: 'Lotes pendentes',
        value: summarized.total_form_batch_pending || 0,
        icon: 'mdi-clipboard-alert',
        className: 'is-warning',
      },
    ];
  }

  private get userStatusLabels() {
    return ['Ativos', 'Inativos'];
  }

  private get userStatusValues() {
    return [
      this.summarized?.total_active_users || 0,
      this.summarized?.total_inactive_users || 0,
    ];
  }

  private get hasUserStatusData() {
    return this.userStatusValues.some((value) => value > 0);
  }

  private get allRegisters() {
    return this.topStats(this.detailed?.all_registers);
  }

  private get byUser() {
    return this.topStats(this.detailed?.by_user);
  }

  private get byUserRoles() {
    return this.topStats(this.detailed?.by_user_roles, 6);
  }

  private get visitedPages() {
    return readVisitedPages(this.$store);
  }

  private get maxGroupTotal() {
    return Math.max(...this.byUserRoles.map((item) => item.total || 0), 1);
  }

  public hasModuleAccess(val) {
    return readHasModuleAccess(this.$store)(val);
  }

  public async mounted() {
    if (!readUserProfile(this.$store)) {
      await dispatchGetUserProfile(this.$store);
    }
    await dispatchGetUserProfileStats(this.$store);
  }

  private topStats(items: IUserStatsItem[] = [], limit = 8) {
    return [...items]
      .filter((item) => item && (item.total || 0) > 0)
      .sort((a, b) => (b.total || 0) - (a.total || 0))
      .slice(0, limit);
  }

  private chartLabels(items: IUserStatsItem[]) {
    return items.map((item) => item.title || 'Sem título');
  }

  private chartValues(items: IUserStatsItem[]) {
    return items.map((item) => item.total || 0);
  }

  private rankingPercent(value: number) {
    return Math.round((value / this.maxGroupTotal) * 100);
  }

  private formatNumber(value: number) {
    return value.toLocaleString('pt-BR');
  }
}
